import { FormStep } from "~/components/ui/form-step";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { PageTransition } from "~/components/page-transition";
import { useLocation, routeLoader$ } from "@builder.io/qwik-city";

import { isUserExist, getUserPreferences } from "~/lib/user.db";
import { coachDiscoverySteps } from "~/lib/schemas/coach-discovery";

import {
  Slot,
  useTask$,
  useStore,
  useSignal,
  component$,
  useContextProvider,
  useComputed$,
} from "@builder.io/qwik";

import { isServer } from "@builder.io/qwik/build";
import {
  UserPreferencesContext,
  UserPreferencesFormContext,
} from "~/contexts/user-preferences";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";

import type { UserPreferencesSelect } from "~/lib/schemas";

const defaultValues = {
  goals: [],
  otherGoal: undefined,
  startTime: "",
  support: "",
  coachingFormats: "",
  coachingApproaches: [],
  coachingPackages: [],
  coachGender: "",
  coachAges: [],
  userPersonality: [],
  coachPersonality: [],
  faithIntegration: [],
  preferredLanguages: ["English"],
  personalContext: "",
  sessionDeliveryMethods: "",
  prefersCoachLocation: "No preference",
  coachLocationRegion: undefined,
  coachLocationCountry: undefined,
  otherFaithIntegration: undefined,
};

export const useUserPreferences = routeLoader$(async ({ cookie }) => {
  const userId = cookie.get("userId")?.value;

  if (!userId || !(await isUserExist(userId))) {
    return defaultValues;
  }

  if (isServer) {
    const userPreferences = await getUserPreferences(userId);
    if (!userPreferences) {
      return defaultValues;
    }
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  }
});

export default component$(() => {
  const location = useLocation();
  const userPreferences = useUserPreferences();
  const preferences = useSignal(
    defaultValues as Partial<UserPreferencesSelect>,
  );

  const formState = useStore({
    steps: Array(10).fill(false),
    currentStepIndex: 0,
  });

  const isShowProgressBar = useComputed$(
    () => !location.url.pathname.includes("processing"),
  );

  useTask$(({ track }) => {
    track(() => location.url.pathname);

    const match = location.url.pathname.match(/\/discovery\/steps\/(\d+)/);
    if (match && match[1]) {
      const stepNumber = parseInt(match[1], 10);
      formState.currentStepIndex = stepNumber - 1;
    }
  });

  useTask$(() => {
    if (isServer) {
      preferences.value = { ...preferences.value, ...userPreferences.value };
    }
  });

  useContextProvider(UserPreferencesContext, preferences);
  useContextProvider(UserPreferencesFormContext, formState);

  return (
    <div class="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumb
        class="mb-6"
        items={[
          { label: "Home", href: "/" },
          { label: "Find my dream coach", href: "/discovery" },
        ]}
      />

      {isShowProgressBar.value && (
        <FormStep.Progress
          totalSteps={coachDiscoverySteps.length}
          currentIndex={formState.currentStepIndex}
        />
      )}
      <div class="relative w-full overflow-visible">
        <PageTransition>
          <Slot />
        </PageTransition>
      </div>
    </div>
  );
});
